<template>
  <div>
    <div
      v-for="(block, blockIndex) in specimenBlocks"
      :key="blockIndex"
      class="font-specimen-block-container"
      :style="getBackgroundStyle(block)"
    >
      <div
        class="font-specimen-block-inner-container site-margin"
      >
        <component
          v-for="(specimen, specimenIndex) in block.specimens"
          :key="specimen.id"
          :is="getComponentType(specimen.variationType)"
          :name="specimen.fontTitleOverride"
          :sampleText="specimen.text"
          :weight="getWeight(specimen)"
          :id="generateId(blockIndex, specimenIndex)"
          :options="getOptions(specimen)"
          v-bind="getVariableProps(specimen)"
          :defaultValue="getDefaultSliderValue(blockIndex, specimenIndex)"
          :style="{ fontFamily: getFontFamilyName(specimen)}"
		      :fontColour="getFontColour(specimen)"
          :titleColour="getTitleColour(specimen)"
          :backgroundColour="getBackgroundColourString(block)"
          :defaultFontFamily="getFontFamilyName(specimen)"
        />
        <div class="font-speciment-block-cta">
          <a
            v-if="block.cta"
            class="button"
            :href="block.cta.url"
            :class="getCtaClass(block.cta.colour)"
          >
            {{ block.cta.text }}
          </a>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { defineComponent } from "vue";
import FontSpecimenElement from "./elements/FontSpecimenElement.vue";
import FontSpecimenVariableElement from "./elements/FontSpecimenVariableElement.vue";

export default defineComponent({
  components: {
    FontSpecimenElement,
    FontSpecimenVariableElement,
  },
  data() {
    return {
      specimenBlocks: [],
    };
  },
  async mounted() {
    const routePath = window?.location?.pathname || "/";
    const response = await fetch(`/umbraco/delivery/api/v1/content/item/${routePath}`);
    const data = await response.json();
    const blocks = (data.properties?.components?.items || [])
      .filter((item) => item?.content?.contentType === "fontSpecimenBlock")
      .map((item) => {
        const specimenItems = item?.content?.properties?.specimens?.items || [];
        const ctaItem = item?.content?.properties?.cTA?.items?.[0]?.content?.properties;

        const specimens = specimenItems.map((spec) => {
        const props = spec.content?.properties || {};

        const dropdownFonts =
          props.dropdownFonts?.items?.map((item) => {
            const fontFile = item.content?.properties?.font?.[0];
            return {
              fontName: item.content?.properties?.fontName,
              url: fontFile?.url,
            };
          }) || [];

          return {
            id: spec.content?.id,
            variationType: props.variationType,
            fontTitleOverride: props.fontTitleOverride,
            text: props.text,
            defaultFont: props.font?.[0] || null,
            fontColour: props.fontColour,
            titleColour: props.titleColour,
            dropdownFonts,
          };
        });

        const backgroundColor = item?.content?.properties?.backgroundColour?.color || "ffffff";
        const cta = ctaItem
          ? {
              text: ctaItem.text,
              url: ctaItem.destination?.[0]?.route?.path || "#",
              colour: `#${ctaItem.colour || "000000"}`
            }
          : null;

        return {
          specimens,
          backgroundColor: `#${backgroundColor}`,
          cta,
        };
      });

    this.injectFonts(blocks);
    this.specimenBlocks = blocks;
  },
  methods: {
    getComponentType(variationType) {
      return variationType === "Doppler"
        ? "FontSpecimenVariableElement"
        : "FontSpecimenElement";
    },
    getWeight(specimen) {
      return specimen.defaultFont ? specimen.defaultFont.name : "";
    },
    getFontColour(specimen) {
      const fontColour = specimen.fontColour?.color || "000000";
      return `#${fontColour}`;
    },
    getTitleColour(specimen) {
      const titleColour = specimen.titleColour?.color || "000000";
      return `#${titleColour}`;
    },
    getBackgroundStyle(block) {
      const color = block.backgroundColor?.toLowerCase();
      if (color === "#0b1c36") {
        return { background: "linear-gradient(to right, #104B81, #0B1C36)" };
      }
      return { background: color || "#ffffff" };
    },
    getBackgroundColourString(block) {
      const color = block.backgroundColor?.toLowerCase();
      return color || "#ffffff";
    },
    getFontFamilyName(specimen) {
      if (!specimen.defaultFont?.url) return "inherit";
      return specimen.defaultFont.url.split("/").pop().split(".")[0];
    },
    getOptions(specimen) {
      if (specimen.variationType === "Weight") {
        return specimen.dropdownFonts.map((font) => {
          return {
            label: font.fontName,
            fontFamily: font.url.split("/").pop().split(".")[0],
          };
        });
      }
      return null;
    },
    getVariableProps(specimen) {
      if (specimen.variationType !== "Doppler") return {};
      return {
        min: 100,
        max: 900,
        step: 100,
        defaultValue: 400,
      };
    },
    getDefaultSliderValue(blockIndex, specimenIndex) {
      const isFirst = blockIndex === 0 && specimenIndex === 0;
      const isSecond = blockIndex === 0 && specimenIndex === 1;
      const isLastBlock = blockIndex === this.specimenBlocks.length - 1;
      const isLastSpecimen = specimenIndex === this.specimenBlocks[blockIndex].specimens.length - 1;
      const isLast = isLastBlock && isLastSpecimen;

      if (isFirst) return 100;
      if (isSecond) return 400;
      if (isLast) return 900;
      return undefined;
    },
     getCtaClass(colour) {
      const cleaned = colour?.toLowerCase();
      if (cleaned === "#1d1d1d") return "internal-black";
      if (cleaned === "#ffffff") return "internal-white";
      return "internal-black";
    },
    generateId(blockIndex, specimenIndex) {
      return `fs-${blockIndex}-${specimenIndex}`;
    },
    injectFonts(blocks) {
      const fontUrls = new Set();

      blocks.forEach((block) => {
        block.specimens.forEach((specimen) => {
          const allFonts = [specimen.defaultFont, ...specimen.dropdownFonts];
          allFonts.forEach((font) => {
            if (font?.url) fontUrls.add(font.url);
          });
        });
      });

      fontUrls.forEach((url) => {
        const fontName = url.split("/").pop().split(".")[0];

        if (!document.head.querySelector(`style[data-font="${fontName}"]`)) {
          const style = document.createElement("style");
          style.setAttribute("data-font", fontName);
          style.textContent = `
            @font-face {
              font-family: '${fontName}';
              src: url('${url}') format('${url.endsWith("woff2") ? "woff2" : "woff"}');
              font-display: swap;
            }
          `;
          document.head.appendChild(style);
        }

        if (!document.head.querySelector(`link[data-preload-font="${fontName}"]`)) {
          const link = document.createElement("link");
          link.setAttribute("rel", "preload");
          link.setAttribute("as", "font");
          link.setAttribute("href", url);
          link.setAttribute("type", url.endsWith(".woff2") ? "font/woff2" : "font/woff");
          link.setAttribute("crossorigin", "anonymous");
          link.setAttribute("data-preload-font", fontName);
          document.head.appendChild(link);
        }
      });
    },

  },
});
</script>